/**
 * Internal QA: force Mandrill to "reject" the Step-5 invitations, so the
 * send-failure branches are reachable (EDUS-2855).
 *
 * The existing forced-failure lever (`qaEndpointFailKey` on `invite-send`) fails
 * the HTTP REQUEST, which is a genuinely different code path: the client's
 * `error` callback runs and you get the generic banner. The bug that was fixed on
 * 2026-09-22 lives in the SUCCESS path — `sendInvites` returns HTTP 200 with a
 * `failures[]` array, and the client used to discard it. Without this lever the
 * branch that reads that array has no way to be exercised against a running
 * system at all.
 *
 * Written by the platform-admin QA controller (test domains only, ~1h TTL) and
 * read by `OnboardingInviteService.sendInvites`, which skips the real Mandrill
 * call and synthesizes the outcome. Defined here so the writer and the reader
 * share one source and cannot drift on the key string.
 */

/**
 * How much of the send to fail.
 *
 * `partial` exists as its own mode because it is the interesting one: those
 * recipients genuinely were invited, so the UI has to distinguish "nobody got
 * it, retry freely" from "some got it, do not re-invite them". A single boolean
 * would only ever produce the easy case.
 */
export const QA_INVITE_REJECT_MODES = ['all', 'partial'] as const;

export type QaInviteRejectMode = (typeof QA_INVITE_REJECT_MODES)[number];

/** What each mode does, for the console. */
export const QA_INVITE_REJECT_LABELS: Record<QaInviteRejectMode, string> = {
  all: 'Reject every recipient (sent: 0)',
  partial: 'Reject every recipient after the first',
};

/**
 * The rejection Mandrill would have reported. Fixed text so the console can tell
 * a QA-simulated rejection from a real one at a glance.
 */
export const QA_INVITE_REJECT_REASON = 'QA override: simulated rejection';

export function qaInviteRejectKey(customerId: number): string {
  return `qa_invite_reject_${customerId}`;
}
